/**
 * Stage 4 and Stage 5 show one set of items (Peter, 2026-10-23):
 * "Stage 4 edits MUST be mirrored in Stage 5. and Stage 5 edits MUST be
 * mirroed in Stage 4." and "None of them are "Authoritative" over the other."
 *
 * Stage 4 keeps the items in `scope`; the quote keeps them in
 * `quoteData.scope`, and the PDF, print and email are built from Stage 5. The
 * two used to be reconciled by an edit-timestamp contest that the two sides
 * stamped differently, so either side could silently lose. These helpers keep
 * one set of items instead:
 *
 * - `pairRows` decides, after any change, which copy moved and gives the other
 *   the same items. When both moved at once, a three-way merge keeps each
 *   side's changes.
 * - `mergeStage5EditsIntoStage4` is that three-way merge: every field one side
 *   changed is kept; an edit beats a delete on either side.
 * - `reconcileStoredRows` handles a project saved before this rule, whose two
 *   copies may already differ. It picks the later copy only on evidence (the
 *   quote's binding receipt) and otherwise reports a conflict for the builder
 *   to choose. The copy not kept is archived, never dropped.
 * - `stage5RowsMirrorKey` is what a reader of the quote sees of its items; the
 *   print, PDF and email guard compares it.
 *
 * See docs/stage4-stage5-mirror/DESIGN.md.
 */
import { stableStringify } from './quote-snapshot-diff';
import { scopeMergeKeys } from './quote-scope-merge';
import { findScopeIdentityMatch } from './quote-external-merge';
import { normaliseQuotePunctuation } from './scope-descriptions';
import {
    ROWS_MIRROR_CONTRACT_VERSION,
    hasFreshAuthoredQuoteForScope,
    quoteRowsReviewedMatch,
    rowsReviewedFingerprintHash,
    scopeFingerprintForQuoteAuthoring,
} from './auto-pipeline';

type Row = Record<string, any>;

function isRow(value: unknown): value is Row {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function rowsOf(value: unknown): Row[] {
    return (Array.isArray(value) ? value : []).filter(isRow);
}

// An absent field, null, an empty string and an empty list all mean "nothing
// here". Display set-up adds `images: []` or `notes_to_client: ''` to rows that
// had no such field; that is not an edit by anyone.
function normaliseEmpty(value: unknown): unknown {
    if (value === undefined || value === null) return null;
    if (typeof value === 'string' && value.trim() === '') return null;
    if (Array.isArray(value) && value.length === 0) return null;
    return value;
}

function sameValue(left: unknown, right: unknown): boolean {
    if (left === right) return true;
    const a = normaliseEmpty(left);
    const b = normaliseEmpty(right);
    if (a === b) return true;
    if (a === null || b === null) return false;
    try {
        return stableStringify(a) === stableStringify(b);
    } catch {
        return false;
    }
}

function sameRow(left: Row, right: Row): boolean {
    if (left === right) return true;
    const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
    for (const key of keys) {
        if (!sameValue(left[key], right[key])) return false;
    }
    return true;
}

/** True when two row lists are the same items, field for field, in order. */
export function sameRows(left: unknown, right: unknown): boolean {
    if (left === right) return true;
    const a = rowsOf(left);
    const b = rowsOf(right);
    if (a.length !== b.length) return false;
    return a.every((row, index) => sameRow(row, b[index]!));
}

function uniqueMatch(row: Row, rows: Row[]): number {
    const match = findScopeIdentityMatch(row, rows);
    return match.status === 'unique' ? match.index : -1;
}

/**
 * Insert `row` into `output` right after the nearest earlier row of `order`
 * that is already in `output`, so a row keeps its place among its neighbours.
 */
function insertAfterNeighbour(output: Row[], row: Row, order: Row[], index: number): void {
    for (let i = index - 1; i >= 0; i -= 1) {
        const at = uniqueMatch(order[i]!, output);
        if (at >= 0) {
            output.splice(at + 1, 0, row);
            return;
        }
    }
    output.unshift(row);
}

function mergeRowFields(local: Row, base: Row, other: Row): Row {
    const merged: Row = { ...local };
    const keys = new Set([...Object.keys(local), ...Object.keys(base), ...Object.keys(other)]);
    for (const key of keys) {
        const l = local[key];
        const b = base[key];
        const o = other[key];
        // Only the other side changed this field: take it. Otherwise this
        // side's value stands, including when both changed it.
        if (sameValue(l, b) && !sameValue(o, b)) {
            if (o === undefined) delete merged[key];
            else merged[key] = o;
        }
    }
    return merged;
}

/**
 * Three-way merge of two changed copies of the same items.
 *
 *   editedRows  - one side's rows now (Stage 5's when the editor publishes)
 *   baseRows    - the rows both sides last agreed on
 *   currentRows - the other side's rows now
 *
 * Every field only one side changed is kept from that side. A field both
 * sides changed keeps `editedRows`' value. A row one side deleted goes,
 * unless the other side edited it after `baseRows`. Additions on either side
 * are kept in place. The order is `editedRows`' order.
 */
export function mergeStage5EditsIntoStage4({
    editedRows: editedInput,
    baseRows: baseInput,
    currentRows: currentInput,
}: {
    editedRows: unknown;
    baseRows: unknown;
    currentRows: unknown;
}): Row[] {
    const edited = rowsOf(editedInput);
    const base = rowsOf(baseInput);
    const current = rowsOf(currentInput);
    const output: Row[] = [];

    for (const localRow of edited) {
        const baseIndex = uniqueMatch(localRow, base);
        const currentIndex = uniqueMatch(localRow, current);
        if (baseIndex < 0) {
            output.push(currentIndex >= 0 ? { ...current[currentIndex]!, ...localRow } : localRow);
            continue;
        }
        const baseRow = base[baseIndex]!;
        if (currentIndex < 0) {
            if (!sameRow(localRow, baseRow)) output.push(localRow);
            continue;
        }
        output.push(mergeRowFields(localRow, baseRow, current[currentIndex]!));
    }

    current.forEach((currentRow, index) => {
        if (uniqueMatch(currentRow, edited) >= 0) return;
        const baseIndex = uniqueMatch(currentRow, base);
        if (baseIndex >= 0 && sameRow(currentRow, base[baseIndex]!)) return;
        insertAfterNeighbour(output, currentRow, current, index);
    });
    return output;
}

/**
 * After a change, give both copies the same items.
 *
 *   previousStage4 / previousStage5 - each copy as both last agreed. They are
 *       one array once paired; a project saved before this rule can start as
 *       two arrays that print the same items (equivalent), and each side's
 *       move is then judged against its own copy.
 *   stage4 / stage5 - each copy now
 *
 * Returns the one array both copies should hold, or null when nothing moved.
 */
export function pairRows({
    previousStage4,
    previousStage5,
    stage4,
    stage5,
}: {
    previousStage4: unknown;
    previousStage5: unknown;
    stage4: unknown;
    stage5: unknown;
}): Row[] | null {
    if (stage4 === stage5) return null;
    const stage4Moved = stage4 !== previousStage4;
    const stage5Moved = stage5 !== previousStage5;
    if (!stage4Moved && !stage5Moved) return null;
    const stage4Rows = Array.isArray(stage4) ? stage4 : [];
    const stage5Rows = Array.isArray(stage5) ? stage5 : [];
    if (sameRows(stage4Rows, stage5Rows)) return stage4Rows;
    if (stage4Moved && !stage5Moved) return stage4Rows;
    if (stage5Moved && !stage4Moved) return stage5Rows;
    return mergeStage5EditsIntoStage4({ editedRows: stage5Rows, baseRows: previousStage4, currentRows: stage4Rows });
}

function words(value: unknown): string {
    return normaliseQuotePunctuation(String(value ?? '')).replace(/\s+/g, ' ').trim();
}

function priceText(value: unknown): string {
    const raw = String(value ?? '').replace(/[^0-9.-]/g, '');
    const parsed = Number(raw);
    return raw !== '' && Number.isFinite(parsed) ? parsed.toFixed(2) : '';
}

function lineTexts(row: Row): string[] {
    return (Array.isArray(row.inclusions) ? row.inclusions : [])
        .map((entry: unknown) => words(typeof entry === 'string' ? entry : (isRow(entry) ? (entry.text ?? entry.label ?? '') : '')))
        .filter(Boolean);
}

function clientDescription(row: Row): string {
    return words(row.descriptionBrief ?? row.description_brief ?? row.clientDescription ?? row.description);
}

function rowMirrorFields(row: Row) {
    return [
        words(row.title),
        clientDescription(row),
        lineTexts(row),
        words(row.notes_to_client ?? row.notesToClient),
        priceText(row.amount),
    ];
}

/**
 * What a reader of the quote sees of its items, in order: title, description,
 * lines, client note and price, with punctuation and spacing normalised. Two
 * row lists with the same key print the same items.
 */
export function stage5RowsMirrorKey(rowsInput: unknown): string {
    return JSON.stringify(rowsOf(rowsInput).map((row) => [String(row.id ?? ''), ...rowMirrorFields(row)]));
}

/**
 * The same comparison without ids: a quote saved before ids were stamped on
 * its rows matches its Stage 4 rows by position.
 */
function rowsEquivalent(left: Row[], right: Row[]): boolean {
    if (left.length !== right.length) return false;
    return left.every((row, index) => {
        const other = right[index]!;
        if (row.id && other.id && row.id !== other.id) return false;
        return JSON.stringify(rowMirrorFields(row)) === JSON.stringify(rowMirrorFields(other));
    });
}

export const ROWS_MIRROR_ARCHIVE_LIMIT = 5;

// Inline image bytes stay out of an archived version; photo identities stay.
function compactImage(image: unknown): unknown {
    if (!isRow(image)) return image;
    const { data, dataUrl, base64, thumbnail, ...rest } = image;
    void data; void dataUrl; void base64; void thumbnail;
    return rest;
}

/**
 * The quote with a record of an item version that was not kept, newest first,
 * so it can be recovered (quote_data.meta.rowsMirrorArchive). Written only
 * when a load pairs two different stored sets (a backup import, a copy kept
 * on this device), the way the 2026-10-24 settle kept them.
 */
export function withRowsNotKept(quote: Row, record: { kept: string; reason: string; rows: unknown; at: string }): Row {
    const meta = isRow(quote.meta) ? quote.meta : {};
    const history = Array.isArray(meta.rowsMirrorArchive) ? meta.rowsMirrorArchive : [];
    const rows = rowsOf(record.rows).map((row) => (Array.isArray(row.images) ? { ...row, images: row.images.map(compactImage) } : row));
    const entry = { at: record.at, kept: record.kept, reason: record.reason, rows };
    return { ...quote, meta: { ...meta, rowsMirrorArchive: [entry, ...history].slice(0, ROWS_MIRROR_ARCHIVE_LIMIT) } };
}

/**
 * The quote with the live app quote's archive of item versions not kept. The
 * archive is the mirror's bookkeeping and the Stage 5 editor never edits it,
 * so a save or Back from the editor takes it whole from the live quote rather
 * than from the copy the editor opened on (review round 7: a choice made in
 * Stage 5 was lost on the next Save or Back).
 */
export function withLiveRowsMirrorArchive<T>(quote: T, liveQuote: unknown): T {
    if (!isRow(quote)) return quote;
    const liveArchive = isRow(liveQuote) && isRow(liveQuote.meta) && Array.isArray(liveQuote.meta.rowsMirrorArchive)
        ? liveQuote.meta.rowsMirrorArchive
        : null;
    const meta = isRow(quote.meta) ? quote.meta : null;
    if (liveArchive) {
        if (meta?.rowsMirrorArchive === liveArchive) return quote;
        return { ...quote, meta: { ...(meta || {}), rowsMirrorArchive: liveArchive } } as T;
    }
    if (!meta || !Object.prototype.hasOwnProperty.call(meta, 'rowsMirrorArchive')) return quote;
    const { rowsMirrorArchive: _dropped, ...rest } = meta;
    return { ...quote, meta: rest } as T;
}

/**
 * A quote rebuilt from scratch (the AI writer, the deterministic draft) keeps
 * the archive of item versions not kept from the quote it replaces. Without
 * this the stored "version not kept" vanished on the next Regenerate or Quote
 * Run (review W6, 2026-10-23). The newest entries come first.
 */
export function carryRowsMirrorArchive<T>(nextQuote: T, previousQuote: unknown): T {
    if (!isRow(nextQuote) || !isRow(previousQuote) || !isRow(previousQuote.meta)) return nextQuote;
    const carried = Array.isArray(previousQuote.meta.rowsMirrorArchive) ? previousQuote.meta.rowsMirrorArchive : [];
    if (carried.length === 0) return nextQuote;
    const meta = isRow(nextQuote.meta) ? nextQuote.meta : {};
    const own = Array.isArray(meta.rowsMirrorArchive) ? meta.rowsMirrorArchive : [];
    const archive = [...own, ...carried.filter((entry: unknown) => !own.includes(entry))].slice(0, ROWS_MIRROR_ARCHIVE_LIMIT);
    return { ...nextQuote, meta: { ...meta, rowsMirrorArchive: archive } } as T;
}

/**
 * The quote with `rows`, marked as following the one-set rule. The first time
 * a quote is marked, a quote whose own rows were the reviewed rows under the
 * old reading (they equalled `rows`) keeps that standing, so pairing never
 * changes whether a quote counts as current.
 */
export function withRowsMirrorContract(quote: Row, rows: Row[]): Row {
    const meta = quote.meta && typeof quote.meta === 'object' ? quote.meta : {};
    let markedMeta = meta;
    if (meta.rowsMirror !== ROWS_MIRROR_CONTRACT_VERSION) {
        markedMeta = { ...meta, rowsMirror: ROWS_MIRROR_CONTRACT_VERSION };
        if (quoteRowsReviewedMatch(quote, rows)) {
            markedMeta.rowsReviewedFingerprintHash = rowsReviewedFingerprintHash(rows);
        }
    }
    if (quote.scope === rows && markedMeta === meta) return quote;
    return { ...quote, scope: rows, meta: markedMeta };
}

/**
 * One change from outside (previousRows to nextRows) applied to an older
 * version of the rows. The change wins: a row it deleted goes, a field it
 * changed takes its value, a row it added is kept in place. Everything else
 * stays as that version had it, including rows the version has and the
 * change never saw, and rows the version lacks are never brought back.
 */
function applyExternalRowChange(versionRows: Row[], previousRows: Row[], nextRows: Row[]): Row[] {
    const output: Row[] = [];
    for (const row of versionRows) {
        const previousIndex = uniqueMatch(row, previousRows);
        if (previousIndex < 0) {
            output.push(row);
            continue;
        }
        const nextIndex = uniqueMatch(previousRows[previousIndex]!, nextRows);
        if (nextIndex < 0) continue;
        const before = previousRows[previousIndex]!;
        const after = nextRows[nextIndex]!;
        const merged: Row = { ...row };
        const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
        for (const key of keys) {
            if (sameValue(before[key], after[key])) continue;
            if (after[key] === undefined) delete merged[key];
            else merged[key] = after[key];
        }
        output.push(sameRow(merged, row) ? row : merged);
    }
    nextRows.forEach((row, index) => {
        if (uniqueMatch(row, previousRows) >= 0) return;
        if (uniqueMatch(row, output) >= 0) return;
        insertAfterNeighbour(output, row, nextRows, index);
    });
    return output;
}

/**
 * The open editor's Undo/Redo history, carried onto items it just took in
 * from Stage 4 (an HBCF add, Internal Pricing, an Assistant or Stage 4
 * edit). Each remembered version keeps its own differences and gains the
 * change from outside (review M3, 2026-10-23). The change from outside wins
 * (review round 2): Undo never brings back a row it deleted, and Undo of the
 * builder's own added row still removes it after that row was repriced.
 */
export function rebaseEditHistoryOntoExternalRows<H extends { past: any[]; future: any[] }>(
    history: H,
    previousRows: unknown,
    nextRows: unknown,
): H {
    if (!history || (history.past.length === 0 && history.future.length === 0)) return history;
    const rebaseSnapshot = (snapshot: any) => {
        const quote = snapshot?.quote;
        if (!isRow(quote) || !Array.isArray(quote.scope)) return snapshot;
        const scope = applyExternalRowChange(rowsOf(quote.scope), rowsOf(previousRows), rowsOf(nextRows));
        return sameRows(scope, quote.scope) ? snapshot : { ...snapshot, quote: { ...quote, scope } };
    };
    const rebaseEntry = (entry: any) => ({ ...entry, before: rebaseSnapshot(entry.before), after: rebaseSnapshot(entry.after) });
    return { ...history, past: history.past.map(rebaseEntry), future: history.future.map(rebaseEntry) };
}

/**
 * What the open Stage 5 editor is handed: the quote it shows (the view-only
 * copy of it, when it opened on one) with the items both stages hold, so a
 * Stage 4 or Assistant change reaches it at once. An empty Stage 4 beside a
 * quote copy that still holds rows keeps the quote's own rows (not paired
 * yet; the load settles it). When the quote's copy is empty too, the items
 * were deleted, in either stage, and a view-only copy taken when the editor
 * opened must not bring them back (review M4, 2026-10-23).
 */
export function composeEditorQuoteData({
    source,
    scope,
    quoteData,
}: {
    source: unknown;
    scope: unknown;
    quoteData: unknown;
}): unknown {
    if (!isRow(source) || !Array.isArray(source.scope)) return source;
    if (!Array.isArray(scope)) return source;
    // The items and their GST basis travel together: the editor shows the
    // live quote's basis, which a GST change may have moved after it opened.
    const liveBasis = isRow(quoteData) && typeof quoteData.isGstInclusive === 'boolean' ? quoteData.isGstInclusive : null;
    const basis = liveBasis !== null && source.isGstInclusive !== liveBasis ? { isGstInclusive: liveBasis } : null;
    if (source.scope === scope) return basis ? { ...source, ...basis } : source;
    const quoteRows = isRow(quoteData) && Array.isArray(quoteData.scope) ? quoteData.scope : [];
    if (scope.length === 0 && quoteRows.length > 0) return source;
    return { ...source, scope, ...(basis || {}) };
}

/**
 * The quote envelope a save writes: its items are the Stage 4 items being
 * saved, so storage always holds one set. Left as it is when Stage 4 is empty
 * while the quote still has items (a quote whose Stage 4 is not built yet).
 */
export function pairQuoteRowsForSave(
    quote: unknown,
    scope: unknown,
    {
        quoteCurrentForScope = null,
    }: { quoteCurrentForScope?: ((quote: Row, scope: Row[]) => boolean) | null } = {},
): unknown {
    if (!isRow(quote) || !Array.isArray(quote.scope) || !Array.isArray(scope)) return quote;
    if (scope.length === 0 && quote.scope.length > 0) return quote;
    // Already the same items: the envelope keeps its rows exactly as stored.
    const paired = sameRows(quote.scope, scope) ? quote : withRowsMirrorContract(quote, scope as Row[]);
    return withQuoteCurrentMark(paired, scope as Row[], quoteCurrentForScope);
}

/**
 * A one-set quote carries the browser's own answer to "does this quote
 * describe the Stage 4 items it is saved with" (meta.rowsMirrorQuoteCurrent).
 * The server's project list read "the quote's rows equal Stage 4's" as that
 * answer, which every save now makes true; it reads this mark instead
 * (projects.js summary, review W2, 2026-10-23). Only a marked quote gets it,
 * and only a changed answer touches the envelope.
 */
function withQuoteCurrentMark(quote: Row, scope: Row[], quoteCurrentForScope: ((quote: Row, scope: Row[]) => boolean) | null): Row {
    const meta = isRow(quote.meta) ? quote.meta : {};
    if (meta.rowsMirror !== ROWS_MIRROR_CONTRACT_VERSION || typeof quoteCurrentForScope !== 'function') return quote;
    let current: boolean;
    try {
        current = quoteCurrentForScope(quote, scope) === true;
    } catch {
        return quote;
    }
    if (meta.rowsMirrorQuoteCurrent === current) return quote;
    return { ...quote, meta: { ...meta, rowsMirrorQuoteCurrent: current } };
}

/** Two lists of items that print the same (wording, prices and lines, punctuation aside). */
export function rowsPrintTheSame(left: unknown, right: unknown): boolean {
    return rowsEquivalent(rowsOf(left), rowsOf(right));
}

export const _internals = { normaliseEmpty, sameValue, sameRow, rowsEquivalent, rowMirrorFields };
